// « Le pouls » — the app's one, tiny usage counter.
//
// It exists for a single decision: the Conciergerie redesign (owner GO,
// 03/08/2026) will eventually move the Balades from a permanent tab into the
// first rail of « La Cité », and the dossier's honest admission was that the
// cost of that move CANNOT be evaluated — nobody measures how guests enter
// the Balades today. This is that measure, installed BEFORE the change so
// there is a before.
//
// Deliberately not analytics. No identity, no session, no page trail, no
// third party: an event name, a source and a day — nothing else leaves the
// device. Fire-and-forget: a failed beacon must never cost a guest anything.

import { API } from '@/lib/medardApi';

/** The only events the backend accepts — keep the vocabulary closed.
 *  `veillee_open` counts one beat per veillée started on a projection wall,
 *  with the CONTE ID as source — the usage measure the Bibliothèque pricing
 *  decision needs before any price exists. */
export const PULSE_EVENTS = Object.freeze([
    'balade_open',
    'arrival',
    'veillee_open',
    // « La Descente » — the gesture-driven library dive in front of the
    // Bibliothèque (phone scroll, wall arrow/wheel scrub). Three beats answer
    // the only question the feature owner has: do guests live it, finish it,
    // or skip it? Source = the surface ('mobile' | 'mur').
    'descente_commencee',
    'descente_terminee',
    'descente_passee',
]);

// A printed QR may tag its URL with `?src=<slug>` (the bed card ships with
// `?src=carte-lit`; TV and train-ticket QRs will follow). Anything else —
// free text, tracking soup — is not a source and never leaves the device.
const ARRIVAL_SRC = /^[a-z0-9][a-z0-9-]{0,31}$/i;
const ARRIVAL_FLAG = 'medard-pulse-arrival';

/**
 * Count ONE arrival per browsing session when the URL carries `?src=…` —
 * the counter behind « combien de scans du QR ? » (carte de lit, TV, billet
 * du petit train). Same promises as every pulse: no identity, no trail, a
 * bounded slug, and silence on every failure. The sessionStorage latch keeps
 * reloads and SPA navigation from inflating the day's bucket.
 */
export function beatArrivalOnce(
    search = typeof window !== 'undefined' ? window.location.search : '',
    storage = typeof window !== 'undefined' ? window.sessionStorage : null,
) {
    try {
        const src = new URLSearchParams(search || '').get('src');
        if (!src || !ARRIVAL_SRC.test(src)) return;
        if (storage && storage.getItem(ARRIVAL_FLAG)) return;
        pulse('arrival', src.toLowerCase());
        if (storage) storage.setItem(ARRIVAL_FLAG, '1');
    } catch {
        /* never let a counter break the app */
    }
}

export function pulse(event, source) {
    if (!PULSE_EVENTS.includes(event)) return;
    try {
        const url = `${API}/medard/pulse`;
        const body = JSON.stringify({ event, source: String(source || 'unknown').slice(0, 32) });
        // sendBeacon survives tab closes and never blocks; fetch keepalive is
        // the fallback. Both errors are swallowed — the pulse is best-effort.
        if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
            navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
            return;
        }
        if (typeof fetch !== 'undefined') {
            fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body,
                keepalive: true,
            }).catch(() => {});
        }
    } catch {
        /* never let a counter break the app */
    }
}
